// Btw, here is the implementation of the dialog for the chat UI.
// Why I decided not to use the Tamagui dialog
// is cuz we can't position it as defined in the design.
import React from "react";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { createPortal } from "react-dom";
import { View, ViewProps } from "tamagui";

import { Button } from "../../ui";

type ToggleButton = React.ComponentProps<typeof Button>;
type LayoutChangeEvent = Parameters<NonNullable<ToggleButton["onLayout"]>>;
type Layout = LayoutChangeEvent["0"]["nativeEvent"]["layout"];

interface DialogContext {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  triggerLayout: Layout;
  setTriggerLayout: React.Dispatch<React.SetStateAction<Layout>>;
  contentLayout: Layout;
  setContentLayout: React.Dispatch<React.SetStateAction<Layout>>;
}

const DialogContext = React.createContext<DialogContext | null>(null);

export const useDialogContext = () => {
  const context = React.useContext(DialogContext);
  if (!context) {
    throw new Error("useDialogContext must be used within a DialogProvider");
  }
  return context;
};

const DialogBaseProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [open, setOpen] = React.useState(false);
  const [contentLayout, setContentLayout] = React.useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const [triggerLayout, setTriggerLayout] = React.useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  return (
    <DialogContext.Provider
      value={{
        open,
        setOpen,
        contentLayout,
        setContentLayout,
        triggerLayout,
        setTriggerLayout,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

//TODO: should be moved to separate file
//NOTE: this portal works only on web, for native we need to use different approach
const Portal = React.memo<ViewProps>((props) => {
  const { setContentLayout } = useDialogContext();
  return createPortal(
    <View
      id="dialog-portal"
      zIndex={9999}
      onLayout={(e) => setContentLayout(e.nativeEvent.layout)}
      style={{
        position: "fixed",
        inset: 0,
        contain: "strict",
        zIndex: 100,
        maxWidth: "100vw",
        maxHeight: "100vh",
        pointerEvents: "none",
      }}
      {...props}
    />,
    globalThis.document.body,
  );
});

Portal.displayName = "Portal";

const Overlay: React.FC<ViewProps> = ({ style, ...rest }) => {
  const { open, setOpen } = useDialogContext();
  if (!open) return null;
  return (
    <View
      onPress={() => setOpen(false)}
      style={[
        {
          position: "fixed",
          inset: 0,
          contain: "strict",
          zIndex: 101,
          maxWidth: "100vw",
          maxHeight: "100vh",
          pointerEvents: "auto",
        },
        style,
      ]}
      {...rest}
    >
      <View onPress={() => setOpen(false)} width="100%" height="100%" />
    </View>
  );
};

const Trigger: React.FC<ToggleButton & { asChild?: boolean }> = ({
  asChild = false,
  children,
  ...rest
}) => {
  const { setOpen, setTriggerLayout } = useDialogContext();

  if (asChild) {
    const opt = {
      onPress: () => setOpen((prev) => !prev),
      onLayout: (e: LayoutChangeEvent["0"]) =>
        setTriggerLayout({
          ...e.nativeEvent.layout,
        }),
    };
    return React.cloneElement(children, opt);
  }

  return (
    <Button
      onLayout={(e) =>
        setTriggerLayout({
          ...e.nativeEvent.layout,
        })
      }
      onPress={() => setOpen((prev) => !prev)}
      {...rest}
    >
      {children}
    </Button>
  );
};

const Close: React.FC<ToggleButton & { asChild?: boolean }> = ({
  asChild = false,
  children,
  ...rest
}) => {
  const { setOpen } = useDialogContext();

  if (asChild) {
    const opt = {
      onPress: () => setOpen((prev) => !prev),
    };
    return React.cloneElement(children, opt);
  }

  return (
    <Button onPress={() => setOpen((prev) => !prev)} {...rest}>
      {children}
    </Button>
  );
};

type DialogContentProps = React.ComponentProps<typeof Animated.View>;

type Position =
  | "left-bottom"
  | "top-left"
  | "right-top"
  | "right-bottom"
  | "bottom-center"
  | "top-center";

export const resolveLayout = (contentLayout: Layout, triggerLayout: Layout) => {
  return {
    height: triggerLayout.y,
    width: contentLayout.width >= 1200 ? 1200 : contentLayout.width,
  };
};

const resolvePosition = (
  triggerLayout: Layout,
  contentLayout: Layout,
  offset: number,
  position?: Position,
) => {
  //TODO: implement other positions
  switch (position) {
    case "top-left":
      return {
        bottom:
          contentLayout.height - triggerLayout.y - triggerLayout.height / 2,
        left: 0,
      };
    default:
      return {
        bottom: contentLayout.height - triggerLayout.y - triggerLayout.height,
        right: contentLayout.width - triggerLayout.x + offset,
      };
  }
};

const DialogContent: React.FC<DialogContentProps> = ({
  children,
  style,
  ...rest
}) => {
  const { open, contentLayout, triggerLayout } = useDialogContext();
  const progress = useSharedValue(0);

  React.useEffect(() => {
    progress.value = withTiming(open ? 1 : 0, {
      duration: 500,
      easing: Easing.out(Easing.cubic),
    });
  }, [open, progress]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
    transform: [
      { scale: interpolate(progress.value, [0, 1], [0.95, 1]) },
      { translateY: interpolate(progress.value, [0, 1], [20, 0]) },
    ],
  }));

  return (
    <Animated.View
      style={[
        style,
        animatedStyle,

        {
          zIndex: 102,
          pointerEvents: open ? "auto" : "none",
          position: "absolute",
          ...resolveLayout(contentLayout, triggerLayout),
          ...resolvePosition(
            triggerLayout,
            contentLayout,
            contentLayout.width > 1200 ? 22 : 5,
            contentLayout.width > 1200 ? undefined : "top-left",
          ),
        },
      ]}
      {...rest}
    >
      {children}
    </Animated.View>
  );
};
export const Dialog = {
  Dialog: DialogBaseProvider,
  Portal,
  Content: DialogContent,
  Trigger,
  Close,
  Overlay,
  useContext: useDialogContext,
};
